import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  PlayCircle,
  ShieldCheck,
  Image as ImageIcon,
  Truck,
} from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { useTutorial } from "@/context/TutorialContext";
import {
  JUBELIO_GUIDE_SECTIONS,
  JUBELIO_TUTORIAL,
} from "@/lib/tutorials/jubelioShipment";
import { useIsFeatureEnabled } from "@/hooks/useFeatureFlag";

function GuideCard({ section }) {
  return (
    <section
      className="bg-white border border-[#E5E5E5] rounded-2xl p-5 shadow-sm"
      data-testid={`jubelio-guide-section-${section.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#FFEDD5] text-[#E85D04] font-bold grid place-items-center shrink-0">
          {section.number}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg text-[#1A1A1A]">
            {section.title}
          </h3>
        </div>
      </div>
      <p className="text-sm text-[#1A1A1A] leading-relaxed">{section.body}</p>

      {section.note && (
        <div
          className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-start gap-2"
          data-testid={`jubelio-guide-note-${section.id}`}
        >
          <ShieldCheck size={14} className="shrink-0 mt-0.5" />
          <span>{section.note}</span>
        </div>
      )}

      {/* Screenshot utama */}
      <ImageSlot section={section} imageKey="image" altKey="imageAlt" testKey={section.id} />

      {/* Screenshot lanjutan (opsional). Boleh punya subTitle + subBody. */}
      {section.subImage && (
        <div className="mt-4 pt-4 border-t border-[#F0E5C0]">
          {section.subTitle && (
            <h4
              className="font-display font-bold text-base text-[#1A1A1A] mb-1"
              data-testid={`jubelio-guide-subtitle-${section.id}`}
            >
              {section.subTitle}
            </h4>
          )}
          {section.subBody && (
            <p className="text-sm text-[#1A1A1A] leading-relaxed">
              {section.subBody}
            </p>
          )}
          <ImageSlot
            section={{ image: section.subImage, imageAlt: section.subImageAlt, title: section.title }}
            imageKey="image"
            altKey="imageAlt"
            testKey={`${section.id}-sub`}
          />
        </div>
      )}
    </section>
  );
}

function ImageSlot({ section, imageKey = "image", altKey = "imageAlt", testKey }) {
  const [failed, setFailed] = React.useState(false);
  const src = section[imageKey];
  const alt = section[altKey] || section.title;
  const id = testKey || section.id;
  if (!src) {
    return (
      <div
        className="mt-3 border-2 border-dashed border-[#E5E5E5] rounded-xl p-4 flex items-center gap-3 text-xs text-[#7A5440]"
        data-testid={`jubelio-guide-image-placeholder-${id}`}
      >
        <ImageIcon size={18} className="shrink-0" />
        <span>
          Contoh gambar akan ditambahkan segera. Ikuti dulu instruksi tulisan
          di atas.
        </span>
      </div>
    );
  }
  if (failed) {
    return (
      <div
        className="mt-3 border-2 border-dashed border-[#E5E5E5] rounded-xl p-4 text-xs text-[#7A5440]"
        data-testid={`jubelio-guide-image-fallback-${id}`}
      >
        Gambar tidak dapat dimuat. Ikuti instruksi tulisan di atas.
      </div>
    );
  }
  return (
    <figure className="mt-3" data-testid={`jubelio-guide-image-${id}`}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-auto rounded-xl border border-[#E5E5E5] bg-[#FAF9F6] max-h-[540px] object-contain"
        onError={() => setFailed(true)}
      />
      {alt && (
        <figcaption className="text-[11px] text-[#7A5440] mt-1">
          {alt}
        </figcaption>
      )}
    </figure>
  );
}

export default function JubelioTutorialGuide() {
  const navigate = useNavigate();
  const {
    start,
    getProgress,
    resetTutorial,
  } = useTutorial();
  const jubelioEnabled = useIsFeatureEnabled("integration.jubelio");
  const progress = getProgress("jubelio-shipment");
  const status = progress?.status || "not_started";

  const startWalkthrough = () => {
    // Overlay akan otomatis navigate ke route step pertama (/admin/jubelio)
    if (status === "in_progress") {
      start("jubelio-shipment", { resume: true });
    } else {
      if (status === "completed") resetTutorial("jubelio-shipment");
      start("jubelio-shipment", { resume: false });
    }
  };

  return (
    <AdminLayout title="Panduan Jubelio Shipment">
      <div
        className="max-w-3xl space-y-5"
        data-testid="jubelio-tutorial-guide-page"
      >
        <button
          type="button"
          onClick={() => navigate("/admin/tutorials")}
          className="inline-flex items-center gap-1 text-sm text-[#5C5C5C] hover:text-[#1A1A1A]"
          data-testid="back-to-tutorials-btn"
        >
          <ArrowLeft size={14} /> Kembali ke Pusat Panduan
        </button>

        <header>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-[#FFEDD5] text-[#E85D04] grid place-items-center">
              <Truck size={20} />
            </div>
            <div>
              <h1 className="font-display text-3xl font-black text-[#1A1A1A]">
                Hubungkan Jubelio Shipment
              </h1>
            </div>
          </div>
          <p className="text-sm text-[#5C5C5C] max-w-2xl">
            Ikuti panduan bergambar berikut untuk menghubungkan Jubelio ke
            Dynicty, mengatur pengiriman, dan membuat resi pesanan.
          </p>
        </header>

        {!jubelioEnabled && (
          <div
            className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900"
            data-testid="jubelio-feature-unavailable"
          >
            Fitur Jubelio belum tersedia untuk toko ini. Panduan tetap bisa
            Anda baca, tapi halaman Pengaturan Jubelio hanya bisa dibuka
            setelah fitur ini diaktifkan pada paket toko Anda.
          </div>
        )}

        <div className="space-y-4">
          {JUBELIO_GUIDE_SECTIONS.map((s) => (
            <GuideCard key={s.id} section={s} />
          ))}
        </div>

        <div
          className="sticky bottom-2 bg-white/95 backdrop-blur p-3 rounded-xl border border-[#E5E5E5] flex items-center justify-between gap-2 flex-wrap"
          data-testid="jubelio-guide-cta-bar"
        >
          <div className="text-xs text-[#5C5C5C]">
            {status === "in_progress"
              ? `Sedang berjalan · langkah ${(progress?.stepIndex ?? 0) + 1} dari ${JUBELIO_TUTORIAL.steps.length}`
              : status === "completed"
              ? "Panduan interaktif sudah selesai. Anda bisa mengulanginya kapan saja."
              : "Sudah siap? Mulai panduan interaktif di halaman Pengaturan Dynicty."}
          </div>
          <Button
            onClick={startWalkthrough}
            disabled={!jubelioEnabled}
            className="brand-btn-primary rounded-xl font-bold"
            data-testid="jubelio-start-walkthrough-btn"
          >
            <PlayCircle size={16} className="mr-1" />
            {status === "in_progress"
              ? "Lanjutkan Panduan Interaktif"
              : status === "completed"
              ? "Ulangi Panduan Interaktif"
              : "Buka Pengaturan Jubelio"}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
